/**
 * Car damage annotation — shared model + geometry (X-marker version).
 *
 * Insurance-report style: the vehicle illustration stays CLEAN. Users drop
 * simple "X" markers at the exact damage locations. No auto-drawn dents or
 * scratch graphics. Each marker is a free point that can be placed, moved,
 * edited (note) or deleted.
 *
 * Used by:
 *   - CarDamageSelector.jsx       (interactive X placement)
 *   - printInvoice.js             (render the same X marks on the invoice)
 *   - RentalsPage printContract   (render the same X marks on the contract)
 *
 * A damage entry is a POINT: { id, x, y, note }, where x/y are in the SVG
 * viewBox coordinate space, so the printed marks match the UI EXACTLY.
 *
 * Design: single top-down (bird's eye) view — clean professional car outline
 * with wheels, windshields, steering wheel, seats, and body details.
 * viewBox: 0 0 260 480
 */

export const DAMAGE_VIEWBOX = '0 0 260 480';
export const DAMAGE_VIEW_W = 260;
export const DAMAGE_VIEW_H = 480;

// Kept for backward-compat with any importer.
export const DAMAGE_BODY = { x: 0, y: 0, w: 260, h: 480, rx: 0 };

/**
 * Reference regions used ONLY to auto-label where an X sits in the report.
 * Zones cover the full top-view layout (front at top, rear at bottom).
 */
export const DAMAGE_ZONES = [
  { key: 'front',      fr: 'Avant',              ar: 'الأمام',          x: 60,  y: 10,  w: 140, h: 80  },
  { key: 'front_left', fr: 'Avant Gauche',        ar: 'الأمام الأيسر',   x: 10,  y: 10,  w: 70,  h: 100 },
  { key: 'front_right',fr: 'Avant Droit',         ar: 'الأمام الأيمن',   x: 180, y: 10,  w: 70,  h: 100 },
  { key: 'left_side',  fr: 'Côté Gauche',         ar: 'الجانب الأيسر',   x: 10,  y: 100, w: 60,  h: 280 },
  { key: 'right_side', fr: 'Côté Droit',          ar: 'الجانب الأيمن',   x: 190, y: 100, w: 60,  h: 280 },
  { key: 'roof',       fr: 'Toit / Dessus',       ar: 'السقف',           x: 70,  y: 100, w: 120, h: 280 },
  { key: 'rear_left',  fr: 'Arrière Gauche',      ar: 'الخلف الأيسر',    x: 10,  y: 370, w: 70,  h: 100 },
  { key: 'rear_right', fr: 'Arrière Droit',       ar: 'الخلف الأيمن',    x: 180, y: 370, w: 70,  h: 100 },
  { key: 'rear',       fr: 'Arrière',             ar: 'الخلف',           x: 60,  y: 390, w: 140, h: 80  },
];

const S = '#2d3748'; // stroke color (dark slate)
const GLASS = '#bfdbfe'; // glass fill (light blue)
const GLASS_S = '#60a5fa'; // glass stroke
const BODY = '#f8fafc'; // body fill
const SEAT = '#e2e8f0'; // seat fill
const SEAT_S = '#94a3b8'; // seat stroke
const TIRE = '#1e293b'; // tire fill
const RIM = '#f1f5f9'; // rim fill

// ── One alloy wheel (top view: ellipse tyre + rim circle + spokes) ────────────
const _wheel = (cx, cy, tw = 22, th = 14) => {
  let s =
    // Tyre (outer ellipse)
    `<ellipse cx="${cx}" cy="${cy}" rx="${tw}" ry="${th}" fill="${TIRE}" stroke="${S}" stroke-width="1.4"/>` +
    // Rim (inner circle, slightly smaller)
    `<ellipse cx="${cx}" cy="${cy}" rx="${tw - 7}" ry="${th - 5}" fill="${RIM}" stroke="${S}" stroke-width="1"/>` +
    // Hub cap
    `<circle cx="${cx}" cy="${cy}" r="4" fill="#cbd5e1" stroke="${S}" stroke-width="0.8"/>`;
  // 5 spokes
  for (let k = 0; k < 5; k++) {
    const a = (-90 + k * 72) * Math.PI / 180;
    const x1 = cx + Math.cos(a) * 4, y1 = cy + Math.sin(a) * 4;
    const x2 = cx + Math.cos(a) * (tw - 8), y2 = cy + Math.sin(a) * (th - 6);
    s += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="#64748b" stroke-width="1.2"/>`;
  }
  return s;
};

// ── Full top-down car SVG inner content ──────────────────────────────────────
// Coordinate system: 0,0 = top-left. Front of car is at top, rear at bottom.
// Car body occupies roughly x:45–215, y:30–450, with rounded corners.
const _topView = () => {
  // ── Body shell ──────────────────────────────────────────────────────────────
  // Main body outline: narrow front, wider cabin, taper to rear
  const body =
    // Full body with realistic car top-view silhouette
    `<path d="
      M 130 18
      C 110 18, 76 22, 65 35
      C 55 46, 50 60, 50 75
      L 50 115
      C 45 118, 42 122, 42 128
      L 42 352
      C 42 358, 45 362, 50 365
      L 50 400
      C 50 415, 55 428, 65 438
      C 76 448, 110 452, 130 452
      C 150 452, 184 448, 195 438
      C 205 428, 210 415, 210 400
      L 210 365
      C 215 362, 218 358, 218 352
      L 218 128
      C 218 122, 215 118, 210 115
      L 210 75
      C 210 60, 205 46, 195 35
      C 184 22, 150 18, 130 18
      Z
    " fill="${BODY}" stroke="${S}" stroke-width="2.2" stroke-linejoin="round"/>`;

  // ── Front bumper line ────────────────────────────────────────────────────────
  const frontBumper =
    `<path d="M 68 36 Q 130 28 192 36" fill="none" stroke="${S}" stroke-width="1.4" stroke-linecap="round"/>` +
    // Headlights
    `<rect x="52" y="28" width="28" height="14" rx="5" fill="#fef9c3" stroke="${S}" stroke-width="1.2"/>` +
    `<rect x="180" y="28" width="28" height="14" rx="5" fill="#fef9c3" stroke="${S}" stroke-width="1.2"/>`;

  // ── Rear bumper line ─────────────────────────────────────────────────────────
  const rearBumper =
    `<path d="M 68 444 Q 130 452 192 444" fill="none" stroke="${S}" stroke-width="1.4" stroke-linecap="round"/>` +
    // Tail lights
    `<rect x="52" y="438" width="28" height="14" rx="5" fill="#fecaca" stroke="${S}" stroke-width="1.2"/>` +
    `<rect x="180" y="438" width="28" height="14" rx="5" fill="#fecaca" stroke="${S}" stroke-width="1.2"/>`;

  // ── A-pillar / windshield (front) ────────────────────────────────────────────
  const frontGlass =
    `<path d="M 68 108 L 72 80 Q 130 72 188 80 L 192 108 Z"
      fill="${GLASS}" stroke="${GLASS_S}" stroke-width="1.4"/>` +
    // Wiper line
    `<path d="M 78 106 Q 130 98 182 106" fill="none" stroke="${GLASS_S}" stroke-width="1.2" stroke-dasharray="2 2"/>`;

  // ── Rear windshield ──────────────────────────────────────────────────────────
  const rearGlass =
    `<path d="M 68 372 L 72 400 Q 130 408 188 400 L 192 372 Z"
      fill="${GLASS}" stroke="${GLASS_S}" stroke-width="1.4"/>`;

  // ── Side windows (2 each side — representing front & rear door windows) ──────
  const sideWindows =
    // Left front door window
    `<rect x="43" y="128" width="16" height="90" rx="4" fill="${GLASS}" stroke="${GLASS_S}" stroke-width="1.2"/>` +
    // Left rear door window
    `<rect x="43" y="232" width="16" height="90" rx="4" fill="${GLASS}" stroke="${GLASS_S}" stroke-width="1.2"/>` +
    // Right front door window
    `<rect x="201" y="128" width="16" height="90" rx="4" fill="${GLASS}" stroke="${GLASS_S}" stroke-width="1.2"/>` +
    // Right rear door window
    `<rect x="201" y="232" width="16" height="90" rx="4" fill="${GLASS}" stroke="${GLASS_S}" stroke-width="1.2"/>`;

  // ── Door lines ───────────────────────────────────────────────────────────────
  const doors =
    // Left door separator
    `<line x1="52" y1="228" x2="60" y2="228" stroke="${S}" stroke-width="1.2"/>` +
    `<line x1="60" y1="118" x2="60" y2="362" stroke="#cbd5e1" stroke-width="1"/>` +
    // Right door separator
    `<line x1="208" y1="228" x2="200" y2="228" stroke="${S}" stroke-width="1.2"/>` +
    `<line x1="200" y1="118" x2="200" y2="362" stroke="#cbd5e1" stroke-width="1"/>` +
    // Door handles (left)
    `<rect x="43" y="178" width="8" height="18" rx="2" fill="#94a3b8" stroke="${S}" stroke-width="0.8"/>` +
    `<rect x="43" y="282" width="8" height="18" rx="2" fill="#94a3b8" stroke="${S}" stroke-width="0.8"/>` +
    // Door handles (right)
    `<rect x="209" y="178" width="8" height="18" rx="2" fill="#94a3b8" stroke="${S}" stroke-width="0.8"/>` +
    `<rect x="209" y="282" width="8" height="18" rx="2" fill="#94a3b8" stroke="${S}" stroke-width="0.8"/>`;

  // ── Interior — steering wheel (front left position = right side in top view) ─
  const steering =
    // Steering column
    `<circle cx="105" cy="168" r="16" fill="none" stroke="${S}" stroke-width="2"/>` +
    `<circle cx="105" cy="168" r="6" fill="${S}" stroke="${S}" stroke-width="1"/>` +
    `<line x1="89" y1="168" x2="121" y2="168" stroke="${S}" stroke-width="1.6"/>` +
    `<line x1="105" y1="152" x2="105" y2="184" stroke="${S}" stroke-width="1.6"/>`;

  // ── Seats ────────────────────────────────────────────────────────────────────
  const seats =
    // Front seats (row 1)
    `<rect x="68" y="130" width="38" height="58" rx="8" fill="${SEAT}" stroke="${SEAT_S}" stroke-width="1.4"/>` +
    `<rect x="68" y="130" width="38" height="16" rx="8" fill="#cbd5e1" stroke="${SEAT_S}" stroke-width="1"/>` +
    `<rect x="154" y="130" width="38" height="58" rx="8" fill="${SEAT}" stroke="${SEAT_S}" stroke-width="1.4"/>` +
    `<rect x="154" y="130" width="38" height="16" rx="8" fill="#cbd5e1" stroke="${SEAT_S}" stroke-width="1"/>` +
    // Rear seats (row 2) — bench seat
    `<rect x="68" y="290" width="124" height="60" rx="8" fill="${SEAT}" stroke="${SEAT_S}" stroke-width="1.4"/>` +
    `<rect x="68" y="290" width="124" height="16" rx="8" fill="#cbd5e1" stroke="${SEAT_S}" stroke-width="1"/>` +
    // Rear seat divider
    `<line x1="130" y1="306" x2="130" y2="350" stroke="${SEAT_S}" stroke-width="1"/>`;

  // ── Center console ───────────────────────────────────────────────────────────
  const console_ =
    `<rect x="113" y="138" width="34" height="180" rx="6" fill="#e2e8f0" stroke="${S}" stroke-width="1.2"/>` +
    // Gear shift
    `<circle cx="130" cy="228" r="7" fill="#94a3b8" stroke="${S}" stroke-width="1"/>` +
    `<circle cx="130" cy="228" r="3" fill="${S}"/>`;

  // ── Wheels (4 corners, top-view ellipses) ────────────────────────────────────
  const wheels =
    _wheel(21, 110, 18, 28) +  // front-left
    _wheel(239, 110, 18, 28) + // front-right
    _wheel(21, 370, 18, 28) +  // rear-left
    _wheel(239, 370, 18, 28);  // rear-right

  // ── License plate outline (front & rear) ─────────────────────────────────────
  const plates =
    `<rect x="110" y="20" width="40" height="10" rx="2" fill="#f0f4f8" stroke="${S}" stroke-width="1"/>` +
    `<rect x="110" y="450" width="40" height="10" rx="2" fill="#f0f4f8" stroke="${S}" stroke-width="1"/>`;

  return body + wheels + frontBumper + rearBumper + frontGlass + rearGlass +
    sideWindows + doors + console_ + seats + steering + plates;
};

/** The static professional car schematic (no markers). Shared selector + print. */
export const damageDiagramInner = (isAr = false) => {
  // Labels for front/rear orientation
  const lbl = (x, y, t) =>
    `<text x="${x}" y="${y}" text-anchor="middle" font-size="11" font-weight="700" fill="#6b7280" font-family="Arial,sans-serif">${t}</text>`;
  const L = (fr, ar) => (isAr ? ar : fr);

  return (
    _topView() +
    lbl(130, 10, L('↑ AVANT', '↑ الأمام')) +
    lbl(130, 475, L('↓ ARRIÈRE', '↓ الخلف'))
  );
};

// Legacy zone-key -> region for backward-compat with any previously-saved zone data.
const ZONE_ALIASES = {
  front_bumper:       'front',
  rear_bumper:        'rear',
  front_left_fender:  'front_left',
  rear_left_fender:   'rear_left',
  front_right_fender: 'front_right',
  rear_right_fender:  'rear_right',
  front_left_door:    'left_side',
  rear_left_door:     'left_side',
  front_right_door:   'right_side',
  rear_right_door:    'right_side',
  side:               'roof',
  top:                'roof',
};

const zoneByKey = (key) => DAMAGE_ZONES.find((z) => z.key === key);

/** Human label for the region a point falls in (contains, else nearest center). */
export const nearestZoneLabel = (x, y, isAr) => {
  // 1) inside a region?
  const inside = DAMAGE_ZONES.find(
    (z) => x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h
  );
  let z = inside;
  // 2) otherwise nearest region center
  if (!z) {
    let best = Infinity;
    for (const cand of DAMAGE_ZONES) {
      const cx = cand.x + cand.w / 2;
      const cy = cand.y + cand.h / 2;
      const d = (cx - x) ** 2 + (cy - y) ** 2;
      if (d < best) {
        best = d;
        z = cand;
      }
    }
  }
  return z ? (isAr ? z.ar : z.fr) : '';
};

let _idSeq = 0;
const newId = () => `dmg_${Date.now().toString(36)}_${(_idSeq++).toString(36)}`;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Normalize stored data into an array of POINTS: { id, x, y, note }.
 * Accepts the new point format AND legacy zone formats (string or {zone}),
 * converting legacy entries to a point at the region center. Nothing is lost.
 */
export const normalizeDamages = (damages) => {
  if (!Array.isArray(damages)) return [];
  const out = [];
  for (const d of damages) {
    if (d && typeof d.x === 'number' && typeof d.y === 'number') {
      out.push({
        id: d.id || newId(),
        x: clamp(d.x, 0, DAMAGE_VIEW_W),
        y: clamp(d.y, 0, DAMAGE_VIEW_H),
        note: d.note || '',
      });
      continue;
    }
    // legacy zone -> center point
    const key = typeof d === 'string' ? d : d && d.zone;
    if (key) {
      const z = zoneByKey(ZONE_ALIASES[key] || key);
      if (z) {
        out.push({
          id: (d && d.id) || newId(),
          x: z.x + z.w / 2,
          y: z.y + z.h / 2,
          note: (d && d.note) || '',
        });
      }
    }
  }
  return out;
};

export const makeDamagePoint = (x, y, note = '') => ({
  id: newId(),
  x: clamp(x, 0, DAMAGE_VIEW_W),
  y: clamp(y, 0, DAMAGE_VIEW_H),
  note,
});

// X-marker color (insurance red).
export const MARKER_COLOR = '#dc2626';

/** SVG string for a single X marker (index badge optional). */
export const markerSvgString = (x, y, i, { size = 7 } = {}) => {
  const s = size;
  return (
    `<g>` +
    `<circle cx="${x}" cy="${y}" r="${s + 4}" fill="#ffffff" fill-opacity="0.95" stroke="${MARKER_COLOR}" stroke-width="1.4"/>` +
    `<line x1="${x - s}" y1="${y - s}" x2="${x + s}" y2="${y + s}" stroke="${MARKER_COLOR}" stroke-width="2.6" stroke-linecap="round"/>` +
    `<line x1="${x - s}" y1="${y + s}" x2="${x + s}" y2="${y - s}" stroke="${MARKER_COLOR}" stroke-width="2.6" stroke-linecap="round"/>` +
    (i != null
      ? `<circle cx="${x + s + 4}" cy="${y - s - 4}" r="6.5" fill="#111827" stroke="#fff" stroke-width="1"/>` +
        `<text x="${x + s + 4}" y="${y - s - 1.2}" text-anchor="middle" font-size="8" font-weight="700" fill="#fff">${i}</text>`
      : '') +
    `</g>`
  );
};

/**
 * Standalone SVG of the clean car diagram with X marks at the saved points.
 * Used by the print/PDF generators (plain HTML, no React).
 */
export const carDiagramSvg = (damages, { isAr = false, width = 200 } = {}) => {
  const pts = normalizeDamages(damages);
  const marks = pts.map((p, i) => markerSvgString(p.x, p.y, i + 1, { size: 8 })).join('');
  const h = Math.round(width * (DAMAGE_VIEW_H / DAMAGE_VIEW_W));
  return (
    `<svg viewBox="${DAMAGE_VIEWBOX}" width="${width}" height="${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="max-width:100%;height:auto;display:block;margin:0 auto;">` +
    damageDiagramInner(isAr) +
    marks +
    `</svg>`
  );
};

/**
 * Full print block: clean diagram + numbered legend of marks (area + note).
 */
export const carDamageBlockHtml = (damages, { isAr = false } = {}) => {
  const pts = normalizeDamages(damages);
  const title = isAr ? '🚗 مواقع الأضرار المسجلة' : '🚗 Localisation des dommages';

  if (pts.length === 0) {
    // Always show the car schematic so the section stays visible.
    return (
      `<div style="margin-top:18px;padding:12px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;">` +
      `<h3 style="font-size:13px;font-weight:700;color:#334155;margin:0 0 10px 0;">${title}</h3>` +
      `<div style="text-align:center;margin-bottom:8px;">${carDiagramSvg(pts, { isAr, width: 180 })}</div>` +
      `<div style="font-size:12px;color:#166534;font-weight:600;">✅ ${isAr ? 'لم تُسجَّل أية أضرار — السيارة في حالة جيدة.' : 'Aucun dommage constaté — véhicule en bon état.'}</div>` +
      `</div>`
    );
  }

  const rows = pts
    .map((p, i) => {
      const loc = nearestZoneLabel(p.x, p.y, isAr);
      return (
        `<tr>` +
        `<td style="padding:3px 6px;text-align:center;width:22px;"><span style="display:inline-block;width:16px;height:16px;line-height:16px;border-radius:50%;background:#111827;color:#fff;font-size:10px;font-weight:700;">${i + 1}</span></td>` +
        `<td style="padding:3px 6px;font-weight:600;">${loc}</td>` +
        `<td style="padding:3px 6px;color:#555;font-size:11px;">${(p.note || '').replace(/</g, '&lt;')}</td>` +
        `</tr>`
      );
    })
    .join('');

  return (
    `<div style="margin-top:18px;padding:12px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;">` +
    `<h3 style="font-size:13px;font-weight:700;color:#334155;margin:0 0 10px 0;">${title} — ${pts.length} ${isAr ? 'علامة' : pts.length > 1 ? 'marques' : 'marque'}</h3>` +
    `<div style="display:flex;gap:12px;align-items:flex-start;">` +
    `<div style="flex-shrink:0;">${carDiagramSvg(pts, { isAr, width: 180 })}</div>` +
    `<div style="flex:1;">` +
    `<table style="width:100%;border-collapse:collapse;font-size:12px;">` +
    `<thead><tr style="border-bottom:1px solid #cbd5e1;color:#475569;font-size:10px;text-transform:uppercase;">` +
    `<th style="padding:3px 6px;">#</th>` +
    `<th style="padding:3px 6px;text-align:${isAr ? 'right' : 'left'};">${isAr ? 'الموقع' : 'Emplacement'}</th>` +
    `<th style="padding:3px 6px;text-align:${isAr ? 'right' : 'left'};">${isAr ? 'ملاحظة' : 'Note'}</th>` +
    `</tr></thead><tbody>${rows}</tbody></table>` +
    `</div></div></div>`
  );
};

/** Ultra-compact damage block for the tight A4 contract recto. */
export const carDamageCompactHtml = (damages, { isAr = false } = {}) => {
  const pts = normalizeDamages(damages);
  // Always render the car schematic — even with no damages — so the section is
  // permanently visible on the contract.
  const items = pts
    .map((p, i) => {
      const loc = nearestZoneLabel(p.x, p.y, isAr);
      return (
        `<span style="display:inline-flex;align-items:center;gap:3px;margin:0 8px 3px 0;">` +
        `<span style="display:inline-block;width:15px;height:15px;line-height:15px;text-align:center;border-radius:50%;background:#111;color:#fff;font-size:9px;font-weight:700;">${i + 1}</span>` +
        `<span style="font-size:10px;font-weight:600;">${loc}${p.note ? ' (' + p.note.replace(/</g, '&lt;') + ')' : ''}</span>` +
        `</span>`
      );
    })
    .join('');
  const legend = pts.length
    ? `<div style="line-height:1.8;">${items}</div>`
    : `<div style="font-size:9px;color:#16a34a;font-weight:700;">${isAr ? '✅ لا توجد أضرار مسجّلة — السيارة في حالة جيدة.' : '✅ Aucun dommage constaté — véhicule en bon état.'}</div>`;
  return (
    `<div style="margin-top:4px;border:1px solid #999;border-radius:6px;padding:4px 6px;display:flex;gap:8px;align-items:flex-start;">` +
    `<div style="flex-shrink:0;">${carDiagramSvg(pts, { isAr, width: 100 })}</div>` +
    `<div style="flex:1;">` +
    `<div style="font-size:10px;font-weight:900;margin-bottom:3px;">${isAr ? 'مواقع الأضرار:' : 'Localisation des dommages:'}</div>` +
    legend +
    `</div></div>`
  );
};
